/* ===================== 主类: WebPushNotification ===================== */
class WebPushNotification {
  constructor (config = {}) {
    /* ===================== 配置区（按需修改） ===================== */
    this.API_BASE = config.apiBase || 'https://wp.revopoint3d.com'
    this.SW_PATH = config.swPath || '/apps/push/sw.js'

    /* ---- 自动弹出节流：这三个参数决定卡片多久自动弹一次 ----
       AUTO_DELAY       进页面后多少「毫秒」自动弹出卡片。
                        5000 = 5 秒（当前设置）
                        0 或负数 = 永不自动弹，只保留右下角小按钮（纯手动入口模式）
       AUTO_COOLDOWN_H  冷却期，单位「小时」。上次自动弹出后，这段时间内不再自动弹。
                        24  = 每天最多自动弹一次（当前设置）
                        48 / 72 = 两三天一次，更克制
                        4 ~ 6   = 近似「每次打开浏览器弹一次」：关浏览器几小时后再开会重新弹
                        （Web 平台没有生命周期 = 浏览器会话的存储，所以只能用短冷却期近似，
                          无法做到严格意义上「关掉浏览器就重置」）
       AUTO_KEY         localStorage 键名，存上次自动弹出的时间戳。
                        改键名 = 让所有老访客的旧记录失效，等价于给全员重置一次自动弹出机会
                        （想"强制让大家再看到一次"时用这招）
       共同前提：右下角小按钮永远可点，不受以上任何参数影响。 */
    this.AUTO_DELAY = config.autoDelay ?? 5000
    this.AUTO_COOLDOWN_H = config.autoCooldownHours ?? 24
    this.AUTO_COOLDOWN_MS = this.AUTO_COOLDOWN_H * 60 * 60 * 1000
    this.AUTO_KEY = config.autoKey || 'wp_auto_at'
    /* ============================================================ */

    this.fab = document.getElementById('webpush-fab')
    this.card = document.getElementById('webpush-card')
    this.btnOk = document.getElementById('webpush-ok')
    this.btnLater = document.getElementById('webpush-later')
    this.testInput = document.getElementById('sub-input')
    this.btnUnsub = document.getElementById('webpush-unsubscribe')

    const rootEl = document.getElementById('webpush-root')
    this.shopDomain = rootEl?.dataset.shopDomain || ''
    this.customerId = rootEl?.dataset.customerId
      ? JSON.parse(rootEl.dataset.customerId)
      : null
    this.rootUrl = this.getMarketPrefix()
    this.pagePath = location.pathname

    const m = this.detectMarket() // 检测市场
    this.locale = m.locale
    this.market = m.market

    // 运行时状态（调参请改上面配置区，不要动这里）
    this.autoUsed = false // 当前页面实例是否已用掉自动弹出名额
    this.autoTimer = null // 自动弹出的延时器句柄

    // 订阅流程互斥：loading 期间不允许再次触发
    this._subscribing = false

    this.bindEvents()
  }

  /* ===================== 隐私合规监测后再初始化 ===================== */
  loadConsent () {
    if (typeof window?.Shopify?.loadFeatures !== 'function') {
      console.log('[WebPush] consent API unavailable, init')
      this.checkPrivacyAndInit()
      return
    }
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      error => {
        if (error)
          console.log('[WebPush] consent-tracking-api load error', error)
        // 成功/失败都进入门禁（内部按横幅有无分支；customerPrivacy 在使用点读取）
        this.checkPrivacyAndInit()
      }
    )
  }

  /* ===================== 隐私合规检测 ===================== */
  checkPrivacyAndInit () {
    // say('Consent API ready, gating...');

    // 🔥 横幅是否当前可见
    const banner = document.querySelector('#shopify-pc__banner')
    const bannerVisible = !!(banner && banner.getClientRects().length)

    // 🔥 1. 有横幅：只监听用户操作，永远不直接初始化
    if (bannerVisible) {
      // say('Banner exists, waiting for user consent...');
      this.listenForConsent()
      return
    }

    // 🔥 2. 无横幅：直接初始化
    // say('No banner, initializing...');
    this.init()
  }

  /* ===================== 监听用户同意 ===================== */
  listenForConsent () {
    let initialized = false

    const consentHandler = event => {
      if (initialized) return

      // say('visitorConsentCollected event:', event.detail);

      const privacy = window.Shopify?.customerPrivacy || null
      if (privacy) {
        const currentStatus = privacy.marketingAllowed()
        if (currentStatus === true) {
          initialized = true
          // say('User accepted consent, initializing...');
          document.removeEventListener(
            'visitorConsentCollected',
            consentHandler
          )
          this.init()
        } else {
          // say('User declined consent, will not initialize');
          document.removeEventListener(
            'visitorConsentCollected',
            consentHandler
          )
        }
      } else {
        // 🔥 无隐私 API 但有横幅事件，直接初始化
        initialized = true
        // say('Consent event received (no privacy API), initializing...');
        document.removeEventListener('visitorConsentCollected', consentHandler)
        this.init()
      }
    }

    document.addEventListener('visitorConsentCollected', consentHandler)

    // 60秒后清理监听器（防止内存泄漏）
    setTimeout(() => {
      if (!initialized) {
        document.removeEventListener('visitorConsentCollected', consentHandler)
        // say('Consent listener timeout');
      }
    }, 60000)
  }

  /* ===================== 显示 FAB ===================== */
  showWebPushUI () {
    // say('Showing WebPush UI (FAB)');
    this.showFab()

    // AUTO_DELAY <= 0：只留小按钮，永不自动弹
    if (!(this.AUTO_DELAY > 0)) return

    // 冷却期内已自动弹过 / 用户点过 Later → 只保留手动入口，不再自动弹
    if (this.autoPopupUsed()) {
      this.autoUsed = true
      // say('Auto popup in cooldown, FAB only');
      return
    }

    // 启动自动显示卡片的定时器
    this.autoTimer = setTimeout(() => {
      if (this.autoUsed) return
      this.markAutoUsed()
      this.switchToCard()
    }, this.AUTO_DELAY)
  }

  /* 冷却期内是否已经自动弹过：localStorage 存上次自动弹出的时间戳，
     距上次 < AUTO_COOLDOWN_MS 就不再自动弹（手动点 FAB 永远可用）。

     为什么用 localStorage 而不是 sessionStorage：
       sessionStorage 是 per-tab（每个标签页一份），关标签页即清空，只能做到"同标签页刷新不重复"，
       做不到"每个浏览器会话一次"或"每天一次"。Web 平台没有生命周期 = 浏览器会话的存储，
       唯一接近的 session cookie 会被 Chrome"启动时继续上次页面"恢复，且会带到每个请求头、合规上更敏感。
     所以这里用 localStorage + 冷却窗口：24h ≈ 每天一次，4h ≈ 近似每次打开浏览器一次。

     合规：写存储的时机在 init() 之后，而 init() 在 checkPrivacyAndInit() 里受 consent 门禁保护，
       有同意横幅的市场（EU）必然是用户已同意之后才写；无横幅市场（如 US）无此约束。

     无痕模式 / 禁用存储时 getItem/setItem 抛 SecurityError，这里吞掉，退化为内存标记
       （行为等同改动前：刷新会再弹，但至少不会报错中断初始化） */
  autoPopupUsed () {
    try {
      const last = Number(localStorage.getItem(this.AUTO_KEY))
      if (!last) return false
      return Date.now() - last < this.AUTO_COOLDOWN_MS
    } catch (e) {
      return this.autoUsed
    }
  }

  /* 标记"已用掉一次自动弹出名额"：清定时器 + 内存标记 + 把当前时间戳写进 localStorage */
  markAutoUsed () {
    this.autoUsed = true
    if (this.autoTimer) {
      clearTimeout(this.autoTimer)
      this.autoTimer = null
    }
    try {
      localStorage.setItem(this.AUTO_KEY, String(Date.now()))
      console.log(
        '[WebPush] auto popup marked, cooldown =',
        this.AUTO_COOLDOWN_H,
        'h, key =',
        this.AUTO_KEY
      )
    } catch (e) {}
  }

  /* 从当前页面路径第一段推出市场前缀（等价于 Liquid 的 routes.root_url） */
  getMarketPrefix () {
    const seg = location.pathname.split('/').filter(Boolean)[0]
    return seg ? '/' + seg : '/'
  }

  // 检测市场
  detectMarket () {
    const DOMAIN_CONFIG = {
      'revopoint3d-de.myshopify.com': { locale: 'en-us', market: 'us' },
      'revopoint-jp.myshopify.com': { locale: 'ja', market: 'jp' }
    }

    const PATH_CONFIG = {
      '/': { locale: 'en-global', market: 'global' },
      '/fr-fr': { locale: 'fr', market: 'fr-fr' },
      '/de-de': { locale: 'de', market: 'de-de' },
      '/en-eur': { locale: 'en-eur', market: 'en-eur' },
      '/en-ca': { locale: 'en-ca', market: 'en-ca' },
      '/en-hk': { locale: 'en-hk', market: 'en-hk' },
      '/en-au': { locale: 'en-au', market: 'en-au' },
      '/en-uk': { locale: 'en-uk', market: 'en-uk' }
    }

    const DEFAULT = { locale: 'en-global', market: 'global' }

    try {
      if (
        typeof this.shopDomain !== 'string' ||
        typeof this.rootUrl !== 'string'
      ) {
        console.warn('[detectMarket] Invalid input variables')
        return DEFAULT
      }

      for (const [domain, config] of Object.entries(DOMAIN_CONFIG)) {
        if (this.shopDomain.includes(domain)) {
          return config
        }
      }

      if (this.shopDomain.includes('revopoint3d.myshopify.com')) {
        return PATH_CONFIG[this.rootUrl] || DEFAULT
      }

      console.warn(
        `[detectMarket] Unknown configuration: ${this.shopDomain}, ${this.rootUrl}`
      )
      return DEFAULT
    } catch (e) {
      console.error('[detectMarket] Error:', e)
      return DEFAULT
    }
  }

  async init () {
    // 1. 能力检测
    if (
      !('serviceWorker' in navigator) ||
      !('PushManager' in window) ||
      !('Notification' in window)
    ) {
      // say('Browser capabilities not supported');
      return
    }

    // 2. 已拒绝通知 → 不显示任何东西
    if (Notification.permission === 'denied') {
      // say('Notification permission denied');
      return
    }

    // 3. 检查已订阅状态
    const sub = await this.findExistingSubscription()

    if (sub) {
      // 已订阅: 回填 + 访问回写 + 显示取消订阅按钮
      // say('Already subscribed, showing unsubscribe button');
      this.backfillIfNeeded(sub)
      this.reportVisit(sub)
      this.showUnsub()
      return
    }

    // 4. 未订阅：显示 UI
    // say('Not subscribed, showing UI');
    if (this.btnOk) this.btnOk.state = 'idle'
    this.showWebPushUI()
  }

  showFab () {
    if (this.fab) {
      this.fab.style.display = ''
      // say('FAB shown');
    }
  }

  hideFab () {
    if (this.fab) {
      this.fab.style.display = 'none'
      // say('FAB hidden');
    }
  }

  showCard () {
    if (!this.card) return
    // say('Showing card');
    this.card.style.display = ''
    this.card.setAttribute('data-banner-active', 'true')
    this.card.style.transform = 'translateY(1rem)'
    this.card.style.opacity = '0'
    this.card.style.pointerEvents = 'auto'
    requestAnimationFrame(() => {
      this.card.style.transform = 'translateY(0)'
      this.card.style.opacity = '1'
    })
  }

  hideCard () {
    if (!this.card) return
    // say('Hiding card');
    this.card.style.display = 'none'
    this.card.style.pointerEvents = 'none'
  }

  hideAll () {
    this.hideFab()
    this.hideCard()
    this.hideUnsub()
  }

  showUnsub () {
    this.hideFab()
    this.hideCard()
    if (this.btnUnsub) {
      this.btnUnsub.style.display = ''
      // say('Unsubscribe button shown');
    }
  }

  hideUnsub () {
    if (this.btnUnsub) {
      this.btnUnsub.style.display = 'none'
    }
  }

  switchToCard () {
    this.hideFab()
    this.resetCard() // 每次打开都是干净的初始态（上次失败留下的文案要清掉）
    this.showCard()
  }

  bindEvents () {
    if (this.fab) this.fab.addEventListener('click', () => this.onFabClick())
    if (this.btnLater)
      this.btnLater.addEventListener('click', () => this.handleLater())
    if (this.btnUnsub)
      this.btnUnsub.addEventListener('click', () => this.handleUnsubscribe())

    if (this.btnOk) {
      this.btnOk.addEventListener('webpush-subscribe', () => {
        this.handleSubscribe()
      })
    }
  }

  onFabClick () {
    // 用户主动点开：取消待触发的自动弹，并占用本次会话名额（刷新后不再自动弹，下次手动点仍可用）
    this.markAutoUsed()
    this.switchToCard()
  }

  handleLater () {
    this.hideCard()
    this.showFab()
    // 点 Later 等同于"冷却期内别再自动弹"：卡片关掉、FAB 留着当手动入口
    this.markAutoUsed()
    console.log('[WebPush] later clicked -> auto popup off for cooldown window')
  }

  async handleUnsubscribe () {
    try {
      const sub = await this.findExistingSubscription()
      if (sub) {
        await fetch(`${this.API_BASE}/api/unsubscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: sub.endpoint })
        }).catch(() => {})
        await sub.unsubscribe()
      }

      this.hideUnsub()
      this.resetCard() // 下次再打开卡片回到默认文案/按钮
      this.showFab()
      alert('已取消订阅, 将不再收到推送通知（仅测试用）')
    } catch (err) {
      console.error('[webpush] unsubscribe failed', err)
      alert('取消订阅失败,请稍后重试（仅测试用）')
    }
  }

  /* 浏览器识别：先试 UA-CH（Chromium 系），认不出再回退 UA 字符串（Safari / Firefox 不实现 navigator.userAgentData）
     返回：'edge' | 'opera' | 'samsung' | 'brave' | 'chrome' | 'chromium' | 'firefox' | 'safari' | 'other'
     都认不出 → 'other'（不返回任意字符串：userAgent 全串已单独入库，Worker 侧也只收枚举值）
     顺序不能改：Edge / Opera / 三星 / Brave 的 UA 里都含 Chrome 关键字，Safari 关键字则人人都含 */
  detectBrowser () {
    const uaData = navigator.userAgentData
    const brands = (uaData?.brands || []).map(b =>
      String(b.brand || '').toLowerCase()
    )
    const hasBrand = kw => brands.some(b => b.includes(kw))

    // 1) UA-CH：只有 Chromium 系浏览器会走到这里
    //    注：Opera / Samsung Internet / Brave 是否出现在 brands 因版本而异，未命中会落到下面的 UA 字符串
    if (brands.length) {
      if (hasBrand('edge') || hasBrand('microsoft')) return 'edge'
      if (hasBrand('opera')) return 'opera'
      if (hasBrand('samsung')) return 'samsung'
      if (hasBrand('brave')) return 'brave'
      if (hasBrand('chrome')) return 'chrome'
      if (hasBrand('chromium')) return 'chromium'
    }

    // 2) UA 字符串回退：Safari / Firefox / iOS 各家（iOS 上的 Chrome/Firefox 走 CriOS/FxiOS，内核仍是 WebKit）
    const ua = navigator.userAgent || ''
    if (/Edg(?:e|A|iOS)?\//.test(ua)) return 'edge'
    if (/OPR\/|OPiOS\/|Opera/.test(ua)) return 'opera'
    if (/SamsungBrowser\//.test(ua)) return 'samsung'
    if (/Firefox\/|FxiOS\//.test(ua)) return 'firefox'
    if (/CriOS\//.test(ua)) return 'chrome'
    if (/Chrome\/|Chromium\//.test(ua)) return 'chrome'
    if (/Safari\//.test(ua)) return 'safari'

    // 3) 认不出 → 'other'。
    //    这里不再 dump UA-CH 的 JSON：userAgent 全串已经单独存了一份，重复且白占 KV，
    //    而且 /api/subscribe 无鉴权，放开写字符串等于给别人一个灌垃圾的口子。
    //    要排查"为什么没识别出来"，直接看这条订阅的 userAgent 字段即可。
    if (typeof say === 'function')
      say('unknown browser -> other', { ua, uaData })
    return 'other'
  }

  /* categories 仅作为 /api/subscribe 请求体，发请求时直接从按钮的 data-categories 读取（逗号分隔） */
  getCategories () {
    const raw = this.btnOk?.dataset?.categories
    if (!raw) return []
    return raw
      .split(',')
      .map(s => s.trim())
      .filter(s => s !== '')
  }

  async findExistingSubscription () {
    const regs = await navigator.serviceWorker.getRegistrations()
    for (const r of regs) {
      const s = await r.pushManager.getSubscription()
      if (s) return s
    }
    return null
  }

  backfillIfNeeded (sub) {
    if (!this.customerId) return
    const cats = this.getCategories()
    fetch(`${this.API_BASE}/api/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subscription: sub.toJSON(),
        shop: this.shopDomain,
        customerId: this.customerId,
        userAgent: navigator.userAgent,
        userBrowser: this.detectBrowser(),
        pagePath: this.pagePath,
        categories: cats,
        locale: this.locale,
        market: this.market
      })
    }).catch(() => {})
  }

  /* 本地日期 YYYY-MM-DD（按用户所在时区切分，用于"每天最多回写一次"的分桶） */
  /* 北京时间（UTC+8）的年/月/日/时/分/秒 —— 与 Worker 侧 fmtTs 的时区保持一致。
     用 en-CA 直接得到 YYYY-MM-DD；hourCycle:'h23' 避免部分实现把午夜输出成 24:00 */
  bjParts () {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(new Date())
    return Object.fromEntries(parts.map(x => [x.type, x.value]))
  }

  /* 分桶用的纯日期键（北京时间）。必须与 Worker 侧同一时区，否则一天会被拆成两条 */
  todayKey () {
    const p = this.bjParts()
    return `${p.year}-${p.month}-${p.day}`
  }

  /* 与 todayKey 同一时区（北京时间）的完整时刻，仅作展示用。
     day 必须保持纯日期：Worker 侧按 day 做分桶去重，带时分秒会导致一天多条 */
  nowText () {
    const p = this.bjParts()
    return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second}`
  }

  /* 拉当前购物车（匿名也可用），失败返回 null —— 回写是尽力而为，不阻断主流程 */
  async fetchCart () {
    try {
      const res = await fetch('/cart.js')
      if (!res.ok) return null
      return await res.json()
    } catch (e) {
      return null
    }
  }

  /* 页面加载回写：记录"今天来过"，写入 时间戳 + 购物车 + 当次登录的 customerId
     - 节流：localStorage 记当天已回写过的「日期|endpoint」，同一订阅一天最多发一次请求
     - 即使被清掉也没关系，Worker 会按 day 覆盖当天记录，数据上恒定一天一条
     - 滚动保留最近 30 天（第 31 天回写后删掉第 1 天），逻辑在 Worker 侧
     - customerId 按次记录：同一浏览器可能被多个账号用过，不改写订阅的归属 */
  async reportVisit (subscription) {
    if (!subscription || !subscription.endpoint) return
    const day = this.todayKey()
    const atText = this.nowText()
    /* 去重键必须带上 endpoint：退订重订 / 清 SW 重订会换 endpoint，
       只按 day 去重的话，浏览器当天上报过旧订阅就会把新订阅的 visit 也拦掉 */
    const visitStamp = day + '|' + subscription.endpoint

    try {
      // 隐身模式 / 禁用存储时 localStorage 可能抛错，忽略即可（回退到由 Worker 去重）
      if (localStorage.getItem('webpush_visit_day') === visitStamp) return
    } catch (e) {}

    const cart = await this.fetchCart()

    try {
      const res = await fetch(`${this.API_BASE}/api/visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          day,
          atText,
          customerId: this.customerId || null,
          cart: cart
            ? {
                itemCount: cart.item_count,
                totalPrice: cart.total_price,
                items: (cart.items || []).map(i => ({
                  productId: i.product_id,
                  variantId: i.variant_id,
                  quantity: i.quantity,
                  price: i.price
                }))
              }
            : null
        })
      })
      if (res.ok) {
        try {
          localStorage.setItem('webpush_visit_day', visitStamp)
        } catch (e) {}
        // say('visit reported:', day);
      } else {
        console.warn('[webpush] visit report rejected:', res.status)
      }
    } catch (e) {
      // 回写失败静默处理，不影响订阅/UI，但留日志便于排查
      console.warn('[webpush] visit report failed:', e)
    }
  }

  urlBase64ToUint8Array (base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/')
    const raw = atob(base64)
    const out = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
    return out
  }

  /* ===================== 卡片文案 =====================
     default | success | failure：文案全部来自 #WP_content 上的 data-* 属性，
     统一写进 #WP_title / #WP_desc（旧的 WP_text-success / WP_text-failure 隐藏节点已移除） */
  showResultText (type = 'default') {
    const box = document.getElementById('WP_content')
    const titleEl = document.getElementById('WP_title')
    const descEl = document.getElementById('WP_desc')
    if (!box || !titleEl || !descEl) return

    const t = ['default', 'success', 'failure'].includes(type)
      ? type
      : 'default'
    const cap = t.charAt(0).toUpperCase() + t.slice(1)
    const title = box.dataset[`title${cap}`]
    const desc = box.dataset[`desc${cap}`]

    // 防御：locale 缺 key 时 | t 会输出 "Translation missing: ..."，此时不覆盖，保留现有文案
    const usable = v =>
      typeof v === 'string' &&
      v.trim() !== '' &&
      !v.includes('Translation missing')
    if (usable(title)) titleEl.textContent = title
    if (usable(desc)) descEl.textContent = desc

    this.syncActions(t)
    // say('Result text ->', t);
  }

  /* 成功态没有"稍后再说"的意义，隐藏 Later；失败/默认保留，方便用户先关掉 */
  syncActions (type) {
    if (this.btnLater)
      this.btnLater.style.display = type === 'success' ? 'none' : ''
  }

  /* 卡片回到初始态（文案 + 按钮 + 操作区） */
  resetCard () {
    if (this.btnOk) this.btnOk.state = 'idle'
    this.showResultText('default')
  }

  /* 给可能永远 pending 的浏览器 API 上超时兜底，超时按失败走 catch，避免按钮卡死在 loading */
  withTimeout (promise, ms, label) {
    return Promise.race([
      Promise.resolve(promise),
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error(`timeout after ${ms}ms: ${label}`)),
          ms
        )
      )
    ])
  }

  /* requestPermission 兼容层：老浏览器只有 callback 无 promise，且部分环境（iframe 等）会永远 pending */
  requestPermissionSafe () {
    return new Promise(resolve => {
      let settled = false
      const done = p => {
        if (!settled) {
          settled = true
          resolve(p || 'denied')
        }
      }
      setTimeout(() => done('denied'), 10000) // 兜底：10s 无响应（提示未弹出）按 denied 处理
      try {
        const res = Notification.requestPermission(done) // callback 形式
        if (res && typeof res.then === 'function') {
          res.then(done).catch(() => done('denied'))
        }
      } catch (e) {
        done('denied')
      }
    })
  }

  async handleSubscribe () {
    if (this._subscribing) return // 防连点
    this._subscribing = true

    const categories = this.getCategories()
    // say('Subscribe clicked, categories:', categories);

    if (this.btnOk) this.btnOk.state = 'loading'
    this.showResultText('default')

    try {
      const permission = await this.requestPermissionSafe()
      // say('Permission:', permission);

      if (permission !== 'granted') {
        // 拒绝授权 / 浏览器已 denied：回到 idle 允许再点一次，同时给出失败文案
        if (this.btnOk) this.btnOk.state = 'idle'
        this.showResultText('failure')
        // say('Permission not granted:', permission);
        return
      }

      // say('Registering SW:', this.SW_PATH);
      const reg = await this.withTimeout(
        navigator.serviceWorker.register(this.SW_PATH),
        10000,
        'serviceWorker.register'
      )
      // say('SW registered');

      const keyResp = await fetch(`${this.API_BASE}/api/vapid-public-key`)
      if (!keyResp.ok)
        throw new Error('fetch vapid key failed: ' + keyResp.status)
      const { publicKey } = await keyResp.json()
      // say('VAPID key fetched');

      const subscription = await this.withTimeout(
        reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(publicKey)
        }),
        15000,
        'pushManager.subscribe'
      )
      // say('Push subscription created');

      const params = {
        subscription: subscription.toJSON(),
        shop: this.shopDomain,
        pagePath: this.pagePath,
        locale: this.locale,
        market: this.market,
        userAgent: navigator.userAgent,
        userBrowser: this.detectBrowser(),
        // 展示用：访客本地时区的完整时刻，与 visits[].day 同源，避免跨时区出现"日期倒挂"
        createdAtText: this.nowText()
      }

      if (categories?.length > 0) params.categories = categories
      if (this.customerId) params.customerId = this.customerId
      if (this.testInput && this.testInput.value)
        params.testName = this.testInput.value

      // say('Subscribe params:', params);

      const resp = await this.withTimeout(
        fetch(`${this.API_BASE}/api/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params)
        }),
        15000,
        '/api/subscribe'
      )

      if (!resp.ok) throw new Error('subscribe failed: ' + resp.status)

      // 刚订阅成功也回写一条：把当天的访问与购物车补进日志，不 await
      this.reportVisit(subscription)

      // reg.showNotification('订阅成功', { body: '新品上架、促销活动会第一时间通知你' });

      // 更新按钮状态为已订阅，并显示成功文案
      if (this.btnOk) this.btnOk.state = 'subscribed'
      this.showResultText('success')

      // 停留 2s 让用户看到成功文案后，切到取消订阅按钮
      setTimeout(() => {
        this.showUnsub()
      }, 2000)
    } catch (err) {
      console.error('[webpush] Subscribe error:', err)
      // 失败保留 idle：允许用户原地重试
      if (this.btnOk) this.btnOk.state = 'idle'
      this.showResultText('failure')

      // Chrome 走 FCM，必须登录 Google 账号才能注册 push service；
      // 未登录 / 无痕 / 访客模式会抛 AbortError: Registration failed - push service error
      const isChrome =
        /Chrome|Chromium/.test(navigator.userAgent) &&
        !/Edg\//.test(navigator.userAgent)
      if (err?.name === 'AbortError' && isChrome) {
        // say('Subscribe failed: Chrome FCM 需要登录 Google 账号，请退出无痕/访客模式后重试');
      }
    } finally {
      this._subscribing = false
    }
  }

  /* ===================== 无痕不显示 ===================== */
  async maybeIncognito () {
    if (!navigator.storage || typeof navigator.storage.estimate !== 'function')
      return false
    try {
      const est = await navigator.storage.estimate() // { quota, usage }
      const quota = Number(est.quota) || 0
      const usage = Number(est.usage) || 0
      const headroom = quota - usage // 可用余量（字节）
      const PRIVATE_HEADROOM_THRESHOLD = 1024 * 1024 * 1024 // 1 GiB
      const result = headroom > 0 && headroom < PRIVATE_HEADROOM_THRESHOLD
      window.say('maybeIncognito:', result, { quota, usage, headroom })
      return result
    } catch (e) {
      window.say('maybeIncognito 探测失败:', e)
      return false
    }
  }
}

/* ===================== 初始化入口 ===================== */
const initWebPush = () => {
  // say('Initializing WebPushNotification...');

  // 移动端（≤767px）整组件不显示：fab/card 默认 display:none，提前 return 即可避免任何显示
  if (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 767px)').matches
  ) {
    // say('Mobile device detected, skip WebPush');
    return
  }

  const instance = new WebPushNotification()

  const startConsent = () => instance.loadConsent()
  if (document.readyState === 'complete') {
    startConsent()
  } else {
    window.addEventListener('load', startConsent)
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWebPush)
} else {
  initWebPush()
}
